import React, { useEffect, useRef, useState } from 'react';
import Input from '/Foundation/Common/code/Views/Foundation/Common/Input.js';
import Select from '/Foundation/Common/code/Views/Foundation/Common/Select.jsx';
import FileInput from '/Foundation/Common/code/Views/Foundation/Common/FileInput.js';
import Spinner from '/Foundation/Common/code/Views/Foundation/Common/Spinner.js';
import Notification from '/Foundation/Common/code/Views/Foundation/Common/Notifications/Notification.js';

import './SignUp.less';
import { validateEmail, validateNotEmpty, validatePassword, validateFile } from '../Validation.js';

const SignUp = () => {
  const data = window._THULEDATA.ProUser;
  const handledErrorCodes = [1, 10, 11];

  const ref = useRef(null);
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [industryType, setIndustryType] = useState('');
  const [preApprovalCode, setPreApprovalCode] = useState('');
  const [file, setFile] = useState(null);

  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [companyError, setCompanyError] = useState(false);
  const [industryError, setIndustryError] = useState(false);
  const [fileError, setFileError] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [showSpinner, setShowSpinner] = useState(false);
  const [showVerificationField, setShowVerificationField] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState('');

  const [disableIndustry, setDisableIndustry] = useState(false);
  const [disableCode, setDisableCode] = useState(false);

  useEffect(() => {
    if (data.IsAuthenticated) {
      window.location = data.RedirectToAfterLogin;
    }

    if (typeof sheerId === 'undefined' || !sheerId) return;

    sheerId.conversion.listenForVerificationId();

    let urlParams = new URLSearchParams(window.location.search);
    let rewardCode = urlParams.get('rc');
    let programId = urlParams.get('pid');

    if (rewardCode && programId) {
      if (localStorage.proSignupData) {
        let data = JSON.parse(localStorage.proSignupData);
        setEmail(data.registerEmail);
        setPassword(data.registerPassword);
        setFirstName(data.registerFirstName);
        setLastName(data.registerLastName);
        setCompany(data.registerCompany);

        localStorage.removeItem('proSignupData');
      }

      let value = data.Industries.find((industry) => industry.SheerIdProgramId === programId)?.Id;
      setIndustryType(value);
      setPreApprovalCode(rewardCode);
      setDisableIndustry(true);
      setDisableCode(true);
    }
  }, []);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 20;

    const renderTurnstile = () => {
      if (!turnstileContainerRef.current) return;
      if (turnstileWidgetIdRef.current !== null) return;

      if (window.turnstile?.render) {
        turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: data.TurnstileSiteKey,
          theme: 'light',
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => setTurnstileToken(''),
        });
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(renderTurnstile, 300);
      }
    };

    renderTurnstile();
  }, [data.TurnstileSiteKey]);

  useEffect(() => {
    let industry = data.Industries.find((industry) => industry.Id === industryType);
    if (industry) setShowVerificationField(industry.ShowVerificationField);
  }, [industryType]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrorMessage('');
    setSuccessMessage('');

    if (localStorage.getItem('proSignupData')) localStorage.removeItem('proSignupData');

    setShowSpinner(true);

    let isEmailValid = validateEmail(email);
    let isPasswordValid = validatePassword(password);
    let isFirstNameValid = validateNotEmpty(firstName);
    let isLastNameValid = validateNotEmpty(lastName);
    let isCompanyValid = validateNotEmpty(company);
    let isIndustryValid = validateNotEmpty(industryType);

    if (isEmailValid && isPasswordValid && isFirstNameValid && isLastNameValid && isCompanyValid && isIndustryValid) {
      if (showVerificationField && file) {
        if (!validateFile(file)) {
          setFileError(true);
          setShowSpinner(false);
          return;
        }
      }

      let language = _THULEDATA._Language;

      let formData = {
        firstName: firstName,
        lastName: lastName,
        email: email,
        company: company,
        password: password,
        industry: industryType,
        discountCode: preApprovalCode,
        payStubFile: file ? await readUploadedFile(file) : null,
        turnstileToken: turnstileToken,
        language: language,
      };

      let options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      };

      try {
        let response = await fetch('/api/sitecore/prosite/signup', options).then((data) => data.json());

        if (response.Success) {
          if (response.Messages.length > 0 && response.Messages[0].Type === 0) {
            setSuccessMessage(response.Messages[0].Content);
            setEmail('');
            setPassword('');
            setFirstName('');
            setLastName('');
            setCompany('');
            setIndustryType('');
            setPreApprovalCode('');
            setFile(null);
            ref?.current?.reset();
          } else {
            if (
              response.Messages &&
              response.Messages.length > 0 &&
              handledErrorCodes.includes(response.Messages[0].Type)
            ) {
              setErrorMessage(response.Messages[0].Content);
            } else {
              setErrorMessage(data.FormError);
            }
          }
        } else {
          if (
            response.Messages &&
            response.Messages.length > 0 &&
            handledErrorCodes.includes(response.Messages[0].Type)
          ) {
            setErrorMessage(response.Messages[0].Content);
          } else {
            setErrorMessage(data.FormError);
          }
        }
        setShowSpinner(false);
        setTurnstileToken('');
        if (turnstileWidgetIdRef.current !== null && window.turnstile?.reset) {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        }
        scrollToNotification();
      } catch (err) {
        console.error(err);
        setErrorMessage(data.FormError);
        setShowSpinner(false);
        setTurnstileToken('');
        if (turnstileWidgetIdRef.current !== null && window.turnstile?.reset) {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        }
        scrollToNotification();
      }
    }
    setEmailError(!isEmailValid);
    setPasswordError(!isPasswordValid);
    setFirstNameError(!isFirstNameValid);
    setLastNameError(!isLastNameValid);
    setCompanyError(!isCompanyValid);
    setIndustryError(!isIndustryValid);
    setShowSpinner(false);
  };

  const scrollToNotification = () => {
    let position = document.querySelector('#pro-signup-form')?.getBoundingClientRect()?.top;
    if (position) {
      window.scrollTo({ top: position + window.scrollY - 100 });
    }
  };

  const readUploadedFile = (inputFile) => {
    const temporaryFileReader = new FileReader();

    return new Promise((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort();
        reject(new DOMException('Problem parsing input file.'));
      };

      temporaryFileReader.onload = () => {
        resolve(temporaryFileReader.result);
      };
      temporaryFileReader.readAsDataURL(inputFile);
    });
  };

  const displayVerification = (programId) => {
    const verificationUrl = 'https://services.sheerid.com/verify/' + programId + '/';
    sheerId.loadInModal(verificationUrl, {
      mobileRedirect: true,
    });
  };

  return (
    <div className="row">
      <div className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 mb-12 px-6" id="pro-signup-form">
        <hr className="mb-12" />
        {errorMessage && (
          <div className="mb-7">
            <Notification
              Type={'error'}
              Size={'sm'}
              Message={errorMessage}
              ShowIcon={true}
              CloseAction={() => setErrorMessage('')}
            />
          </div>
        )}
        {successMessage && (
          <div className="mb-7">
            <Notification
              Type={'success'}
              Size={'sm'}
              Message={successMessage}
              ShowIcon={true}
              CloseAction={() => setSuccessMessage('')}
            />
          </div>
        )}
        <form id="register-form" noValidate onSubmit={(e) => handleSubmit(e)} ref={ref}>
          <h4 className="mb-7">{data.SignUp}</h4>
          <div className="input-row mb-5">
            <Input
              id={'email'}
              label={data.Email}
              placeholder={data.Email}
              hasError={emailError}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              errorMessage={data.InvalidEmail}
              onBlur={(e) => setEmailError(!validateEmail(e.target.value))}
              type="email"
            />
            <Input
              id="password"
              label={data.Password}
              placeholder={data.Password}
              hasError={passwordError}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              errorMessage={data.WeakPassword}
              onBlur={(e) => setPasswordError(!validatePassword(e.target.value))}
              type="password"
            />
          </div>
          <div className="input-row mb-5">
            <Input
              id="firstname"
              label={data.FirstName}
              placeholder={data.FirstName}
              hasError={firstNameError}
              errorMessage={data.InvalidFirstName}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={(e) => setFirstNameError(!validateNotEmpty(e.target.value))}
              type="text"
            />
            <Input
              id="lastname"
              label={data.LastName}
              placeholder={data.LastName}
              hasError={lastNameError}
              errorMessage={data.InvalidLastName}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={(e) => setLastNameError(!validateNotEmpty(e.target.value))}
              type="text"
            />
          </div>
          <div className="input-row mb-5">
            <Input
              id="company"
              label={data.Company}
              placeholder={data.Company}
              hasError={companyError}
              errorMessage={data.InvalidCompany}
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              onBlur={(e) => setCompanyError(!validateNotEmpty(e.target.value))}
              type="text"
            />
          </div>
          <Select
            id="proUserType"
            options={data.Industries.map((industry) => {
              return { name: industry?.Name, value: industry?.Id };
            })}
            label={data.Industry}
            placeholder={data.Industry}
            hasError={industryError}
            errorMessage={data.InvalidProusertype}
            value={industryType}
            onChange={(e) => {
              setIndustryType(e.target.value);
              setIndustryError(!validateNotEmpty(e.target.value));

              let sheerIdProgramId = data.Industries.find(
                (industry) => industry.Id === e.target.value
              )?.SheerIdProgramId;

              if (sheerIdProgramId) {
                let formData = {
                  registerEmail: email,
                  registerPassword: password,
                  registerFirstName: firstName,
                  registerLastName: lastName,
                  registerCompany: company,
                };

                localStorage.setItem('proSignupData', JSON.stringify(formData));

                displayVerification(sheerIdProgramId);
              }
            }}
            onBlur={() => {}}
            isDisabled={disableIndustry}
            additionalClasses={'skipScroll large'}
          />
          <hr className="mt-7 mb-7" />
          {showVerificationField && (
            <FileInput
              id="file"
              label={data.Paystub}
              placeholder={data.Paystub}
              hasError={fileError}
              errorMessage={data.InvalidFile}
              onChange={(e) => {
                let file = e.target.files[0];
                let validationResult = validateFile(file);
                if (!validationResult) {
                  setFileError(true);
                } else {
                  setFile(e.target.files[0]);
                }
              }}
              supportingText={data.FileInformation}
              accept=".jpg,.jpeg,.png,.pdf"
              additionalClasses={'mb-5'}
            />
          )}
          <Input
            id="preApprovalCode"
            label={data.Discountcode}
            placeholder={data.Discountcode}
            value={preApprovalCode}
            onChange={(e) => setPreApprovalCode(e.target.value)}
            type="text"
            additionalClasses={'mb-8'}
            isDisabled={disableCode}
            supportingText={data.PreApprovalSupportText}
          />
          <div id="proUserSignupTurnstile" className="cf-turnstile mb-4" ref={turnstileContainerRef}></div>
          <div className="toc-Text">{data.SubmitInformation}</div>
          <button className="btn thule-btn-default w-100 mt-5">
            {data.Submit}
            {showSpinner && <Spinner />}
          </button>
          <button
            className="btn thule-btn-secondary w-100 mt-5"
            onClick={(e) => {
              e.preventDefault();
              window.location = data.LoginPageLink;
            }}
          >
            {data.AlreadyRegistered}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignUp;
